import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  IconChevronDown,
  IconHome2,
  IconBox,
  IconReceipt2,
  IconWallet,
  IconHistory,
  IconPhoto,
  IconDiamond,
  IconChartBar,
  IconX,
  IconCircleFilled,
  IconLogout,
  IconSun,
  IconMoon,
} from '@tabler/icons-react';
import AddJenisBarang from '../components/Admin/AddJenisBarang';
import ConfirmationLogOut from '../components/ConfirmationLogOut';
import ModalLogout from './ModalLogout';
import { useTheme } from '../context/ThemeContext';
import logoKD from '../assets/logo_kd.png';

function Sidebar({ sidebarOpen, setSidebarOpen }) {
  const [openSubMenuBarang, setOpenSubMenuBarang] = useState(false);
  const [openSubMenuTransaksi, setOpenSubMenuTransaksi] = useState(false);
  const [openSubMenuCashflow, setOpenSubMenuCashflow] = useState(false);
  const [openSubMenuHistori, setOpenSubMenuHistori] = useState(false);
  const [openSubMenuGaleri, setOpenSubMenuGaleri] = useState(false);
  let [isOpen, setIsOpen] = useState(false);

  const trigger = useRef(null);
  const sidebar = useRef(null);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  const storedSidebarExpanded = localStorage.getItem('sidebar-expanded');
  const [sidebarExpanded, setSidebarExpanded] = useState(
    storedSidebarExpanded === null ? false : storedSidebarExpanded === 'true'
  );

  const [isOpenModalLogout, setIsOpenModalLogout] = useState(false);

  function closeModalLogout() {
    setIsOpenModalLogout(false);
  }

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setSidebarOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  useEffect(() => {
    localStorage.setItem('sidebar-expanded', sidebarExpanded);
    if (sidebarExpanded) {
      document.querySelector('body').classList.add('sidebar-expanded');
    } else {
      document.querySelector('body').classList.remove('sidebar-expanded');
    }
  }, [sidebarExpanded]);

  // Auto-expand submenus based on active routes
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/list-items-admin') || path.includes('/barang-terhapus')) {
      setOpenSubMenuBarang(true);
    }
    if (path.includes('/transaksi-penjualan') || path.includes('/transaksi-pembelian') || path.includes('/list-transaksi-tertunda') || path.includes('/summary')) {
      setOpenSubMenuTransaksi(true);
    }
    if (path.includes('/saldo-kas') || path.includes('/pengeluaran') || path.includes('/laporan-cashflow')) {
      setOpenSubMenuCashflow(true);
    }
    if (path.includes('/histori-transfer-masuk') || path.includes('/histori-transfer-keluar')) {
      setOpenSubMenuHistori(true);
    }
    if (path.includes('/galeri-barang') || path.includes('/galeri-penjualan') || path.includes('/galeri-pembelian')) {
      setOpenSubMenuGaleri(true);
    }
  }, [location.pathname]);

  function closeModal() {
    setIsOpen(false);
  }
  function openModal() {
    setIsOpen(true);
  }

  const navigate = useNavigate();

  function logOut() {
    localStorage.removeItem('role');
    localStorage.removeItem('token');
    localStorage.removeItem('toko');
    localStorage.removeItem('name');
    localStorage.removeItem('tipe_surat');
    navigate('/login');
  }

  const user = localStorage.getItem('role');
  const toko = localStorage.getItem('toko');

  // Stylish link component
  const NavItem = ({ to, icon: Icon, label }) => (
    <NavLink
      to={to}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        `flex flex-row justify-start items-center gap-3 px-4 py-2.5 rounded-xl mb-1 cursor-pointer transition-all duration-200 group ${isActive
          ? 'bg-amber-800/10 dark:from-amber-900/40 dark:to-amber-800/30 text-amber-900 dark:text-amber-300 font-bold shadow-sm border border-amber-800/20 dark:border-amber-700/50'
          : 'text-slate-700 dark:text-slate-300 hover:bg-amber-800/5 dark:hover:bg-slate-800/80 hover:text-amber-900 dark:hover:text-amber-400'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={`w-[22px] h-[22px] transition-colors ${isActive ? 'text-amber-800 dark:text-amber-400' : 'text-slate-600 dark:text-slate-400 group-hover:text-amber-900 dark:group-hover:text-amber-300'
              }`}
            stroke={isActive ? 2.5 : 1.5}
          />
          <span className="truncate">{label}</span>
        </>
      )}
    </NavLink>
  );

  const SubMenuItem = ({ to, label, onClick }) => {
    if (onClick) {
      return (
        <div
          onClick={onClick}
          className="py-2 text-sm text-slate-700 dark:text-slate-300 hover:text-amber-900 dark:hover:text-amber-400 cursor-pointer transition-colors flex items-center gap-2.5 rounded-lg px-2 hover:bg-amber-800/5 dark:hover:bg-slate-800/80 font-medium"
        >
          <div className="w-1.5 h-1.5 rounded-full bg-slate-500 dark:bg-slate-500 transition-colors" />
          <span className="truncate">{label}</span>
        </div>
      );
    }
    return (
      <NavLink
        to={to}
        onClick={() => setSidebarOpen(false)}
        className={({ isActive }) =>
          `py-2 text-sm cursor-pointer transition-colors flex items-center gap-2.5 rounded-lg px-2 hover:bg-amber-800/5 dark:hover:bg-slate-800/80 group ${isActive ? 'text-amber-900 dark:text-amber-400 font-bold bg-amber-800/10 dark:bg-slate-800/70' : 'text-slate-700 dark:text-slate-300 font-medium'
          }`
        }
      >
        {({ isActive }) => (
          <>
            <div
              className={`w-1.5 h-1.5 rounded-full transition-colors ${isActive ? 'bg-amber-800 shadow-[0_0_4px_rgba(180,83,9,0.5)] dark:bg-amber-500' : 'bg-slate-500 dark:bg-slate-500 group-hover:bg-amber-800'
                }`}
            />
            <span className="truncate">{label}</span>
          </>
        )}
      </NavLink>
    );
  };

  const ParentMenu = ({ icon: Icon, label, isOpen: menuOpen, onClick }) => (
    <div
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`flex flex-row justify-between items-center gap-3 px-4 py-2.5 rounded-xl mb-1 cursor-pointer transition-all duration-200 group ${menuOpen
        ? 'bg-amber-800/10 dark:bg-slate-800/70 text-amber-900 dark:text-amber-300'
        : 'text-slate-700 dark:text-slate-300 hover:bg-amber-800/5 dark:hover:bg-slate-800/80 hover:text-amber-900 dark:hover:text-amber-300'
        }`}
    >
      <div className="flex items-center gap-3">
        <Icon
          className={`w-[22px] h-[22px] transition-colors ${menuOpen ? 'text-amber-800 dark:text-amber-400' : 'text-slate-600 dark:text-slate-400 group-hover:text-amber-900 dark:group-hover:text-amber-400'
            }`}
          stroke={menuOpen ? 2 : 1.5}
        />
        <span className={`${menuOpen ? 'font-bold' : 'font-semibold'} truncate`}>{label}</span>
      </div>
      <IconChevronDown
        className={`w-4 h-4 transition-transform duration-200 ${menuOpen ? 'rotate-180 text-amber-800 dark:text-amber-400' : 'text-slate-500 dark:text-slate-500 group-hover:text-amber-900'
          }`}
      />
    </div>
  );

  return (
    <div>
      <ModalLogout isOpenModalLogout={isOpenModalLogout} closeModaLogout={closeModalLogout} logout={logOut} />
      <AddJenisBarang isOpen={isOpen} closeModal={closeModal} />
      <ConfirmationLogOut />

      {/* Mobile Backdrop */}
      <div
        className={`fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        aria-hidden="true"
        onClick={() => setSidebarOpen(false)}
      ></div>

      {/* Sidebar Content */}
      <div
        id="sidebar"
        ref={sidebar}
        className={`flex flex-col w-[17rem] absolute outline-none z-50 left-0 top-0 lg:static lg:left-auto lg:top-auto lg:translate-x-0 h-screen shrink-0 bg-gradient-to-b from-yellow-100 via-amber-100/70 to-yellow-200/60 dark:from-slate-900 dark:via-amber-900/10 dark:to-slate-900 border-r border-amber-300/60 dark:border-amber-700/50 shadow-[4px_0_24px_-10px_rgba(0,0,0,0.05)] dark:shadow-none transition-all duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-amber-50/80 dark:border-slate-700/50">
          <Link to="/" className="flex items-center gap-2.5 group outline-none">
            <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-amber-300/50 dark:border-amber-500/40 shadow-sm shadow-amber-200/50 dark:shadow-amber-900/30 flex-shrink-0">
              <img src={logoKD} alt="Komala Dewi" className="w-full h-full object-cover" />
            </div>
            <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-600 to-amber-800 dark:from-amber-400 dark:to-amber-600 tracking-tight">
              Komala Dewi
            </span>
          </Link>

          {/* Close button (Mobile) */}
          <button
            ref={trigger}
            className="lg:hidden text-slate-400 dark:text-slate-500 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition-colors outline-none"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-controls="sidebar"
            aria-expanded={sidebarOpen}
          >
            <span className="sr-only">Close sidebar</span>
            <IconX size={20} />
          </button>
        </div>

        {/* Links Container */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 custom-scrollbar">
          <div className="space-y-1">
            <NavItem to="/" icon={IconHome2} label="Dashboard" />

            {/* Barang Menu */}
            <div>
              <ParentMenu
                icon={IconBox}
                label="Barang"
                isOpen={openSubMenuBarang}
                onClick={() => setOpenSubMenuBarang(!openSubMenuBarang)}
              />
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${openSubMenuBarang ? 'max-h-80 opacity-100 mb-2' : 'max-h-0 opacity-0'
                  }`}
              >
                <div className="flex flex-col gap-0.5 pl-11 pr-2 pt-1 pb-2 relative">
                  <div className="absolute left-[29px] top-2 bottom-4 w-px bg-slate-100 dark:bg-slate-700" />
                  <SubMenuItem onClick={openModal} label="Jenis Barang" />
                  <SubMenuItem to="/list-items-admin" label="List Barang" />
                  <SubMenuItem to="/barang-terhapus" label="Barang dikeluarkan" />
                </div>
              </div>
            </div>

            {/* Transaksi Menu */}
            <div>
              <ParentMenu
                icon={IconReceipt2}
                label="Transaksi"
                isOpen={openSubMenuTransaksi}
                onClick={() => setOpenSubMenuTransaksi(!openSubMenuTransaksi)}
              />
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${openSubMenuTransaksi ? 'max-h-80 opacity-100 mb-2' : 'max-h-0 opacity-0'
                  }`}
              >
                <div className="flex flex-col gap-0.5 pl-11 pr-2 pt-1 pb-2 relative">
                  <div className="absolute left-[29px] top-2 bottom-4 w-px bg-slate-100 dark:bg-slate-700" />
                  <SubMenuItem to="/transaksi-penjualan" label="Penjualan" />
                  <SubMenuItem to="/transaksi-pembelian" label="Pembelian" />
                  <SubMenuItem to="/list-transaksi-tertunda" label="Penggarus" />
                  <SubMenuItem to="/summary" label="Laporan" />
                </div>
              </div>
            </div>

            {/* Cashflow Menu */}
            <div>
              <ParentMenu
                icon={IconWallet}
                label="Cashflow"
                isOpen={openSubMenuCashflow}
                onClick={() => setOpenSubMenuCashflow(!openSubMenuCashflow)}
              />
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${openSubMenuCashflow ? 'max-h-80 opacity-100 mb-2' : 'max-h-0 opacity-0'
                  }`}
              >
                <div className="flex flex-col gap-0.5 pl-11 pr-2 pt-1 pb-2 relative">
                  <div className="absolute left-[29px] top-2 bottom-4 w-px bg-slate-100 dark:bg-slate-700" />
                  <SubMenuItem to="/saldo-kas" label="Saldo KAS" />
                  <SubMenuItem to="/pengeluaran" label="Transaksi Luar" />
                  <SubMenuItem to="/laporan-cashflow" label="Laporan" />
                </div>
              </div>
            </div>

            {/* Riwayat Logic */}
            {user === 'admin' ? (
              <NavItem to="/riwayat" icon={IconHistory} label="Riwayat" />
            ) : (
              <div>
                <ParentMenu
                  icon={IconHistory}
                  label="Riwayat"
                  isOpen={openSubMenuHistori}
                  onClick={() => setOpenSubMenuHistori(!openSubMenuHistori)}
                />
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${openSubMenuHistori ? 'max-h-80 opacity-100 mb-2' : 'max-h-0 opacity-0'
                    }`}
                >
                  <div className="flex flex-col gap-0.5 pl-11 pr-2 pt-1 pb-2 relative">
                    <div className="absolute left-[29px] top-2 bottom-4 w-px bg-slate-100 dark:bg-slate-700" />
                    <SubMenuItem to="/histori-transfer-masuk" label="Riwayat Barang Masuk" />
                    <SubMenuItem to="/histori-transfer-keluar" label="Riwayat Barang Keluar" />
                  </div>
                </div>
              </div>
            )}

            {/* Galeri Menu */}
            <div>
              <ParentMenu
                icon={IconPhoto}
                label="Galeri"
                isOpen={openSubMenuGaleri}
                onClick={() => setOpenSubMenuGaleri(!openSubMenuGaleri)}
              />
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${openSubMenuGaleri ? 'max-h-80 opacity-100 mb-2' : 'max-h-0 opacity-0'
                  }`}
              >
                <div className="flex flex-col gap-0.5 pl-11 pr-2 pt-1 pb-2 relative">
                  <div className="absolute left-[29px] top-2 bottom-4 w-px bg-slate-100 dark:bg-slate-700" />
                  <SubMenuItem to="/galeri-barang" label="Barang" />
                  <SubMenuItem to="/galeri-penjualan" label="Penjualan" />
                  <SubMenuItem to="/galeri-pembelian" label="Pembelian" />
                </div>
              </div>
            </div>

            {/* Role-specific items */}
            {user !== 'admin' && (
              <NavItem to="/tambah-barang" icon={IconDiamond} label="Tambah Barang" />
            )}

            {user === 'admin' && (
              <NavItem to="/laba-penjualan" icon={IconChartBar} label="Laba Penjualan" />
            )}

          </div>
        </div>

        {/* User Profile / Logout footer */}
        <div className="p-4 border-t border-amber-50/80 dark:border-slate-700/50 bg-slate-50/30 dark:bg-slate-800/30">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="w-full mb-3 py-2 px-3 rounded-xl flex justify-center items-center gap-2 text-sm font-medium transition-all duration-300 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:border-amber-300 dark:hover:border-amber-600 hover:text-amber-600 dark:hover:text-amber-400 hover:shadow-sm"
          >
            {theme === 'light' ? (
              <>
                <IconMoon size={18} className="text-amber-500" />
                <span>Dark Mode</span>
              </>
            ) : (
              <>
                <IconSun size={18} className="text-amber-400" />
                <span>Light Mode</span>
              </>
            )}
          </button>

          <div className="bg-white dark:bg-slate-800 rounded-xl p-3 shadow-sm border border-amber-100/40 dark:border-slate-700/60 flex flex-col gap-3">
            <div className="flex items-center gap-3 px-1">
              <div className="w-10 h-10 rounded-full bg-amber-100/50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 flex items-center justify-center font-bold shadow-inner border border-amber-200/60 dark:border-amber-700/40 shrink-0">
                {user ? user.charAt(0).toUpperCase() : 'U'}
              </div>
              <div className="flex flex-col overflow-hidden">
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 uppercase tracking-wide truncate">
                  {user}
                </span>
                {user !== 'admin' && toko && (
                  <span className="text-xs text-amber-600 dark:text-amber-400 font-medium uppercase truncate">
                    {toko}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={logOut}
              className="w-full py-2 px-3 rounded-lg flex justify-center items-center gap-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-300 font-medium text-sm transition-colors border border-transparent hover:border-red-100 dark:hover:border-red-800/40"
            >
              <IconLogout size={18} />
              <span>Log Out</span>
            </button>
          </div>
        </div>

      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #f1f5f9;
          border-radius: 20px;
        }
        .custom-scrollbar:hover::-webkit-scrollbar-thumb {
          background-color: #cbd5e1;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #1e293b;
        }
        .dark .custom-scrollbar:hover::-webkit-scrollbar-thumb {
          background-color: #334155;
        }
      `}</style>
    </div>
  );
}

export default Sidebar;
